import { Form, Formik } from 'formik';
import { NextPage } from 'next'
import { useTranslation } from 'next-i18next';
import PageLayout from '../../components/layout/PageLayout'
import Panel from '../../components/layout/Panel'
import { ContactFormModel, UserGender } from '@model/dist/esm'
import { Ref, useRef, useState } from 'react';
import RadioGroupControl from '../../components/form-controls/controls/RadioGroupControl';
import { fromEnum } from '../../lib/utils/fromEnum';
import InputTextControl from '../../components/form-controls/controls/InputTextControl';
import InputTextareaControl from '../../components/form-controls/controls/InputTextareaControl';
import ErrorFocus from '../../components/forms/ErrorFocus';
import Button from '../../components/ui/button/Button';
import Footer from '../../components/footer/Footer';
import { postContact } from '../../lib/api/contact.api';
import { useRouter } from 'next/router';
import ReCAPTCHA from "react-google-recaptcha";
import Seo from '../../components/seo/Seo';
import { Routes } from 'lib/routes';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { defaultNs } from 'lib/i18n/defaults';
import * as Sentry from '@sentry/browser';
import RetryableReCAPTCHA from 'components/forms/RetryableReCaptcha';

const ContactPage: NextPage = () => {

    const { t } = useTranslation();
    const { locale, ...router } = useRouter();
    const reCAPTCHARef: Ref<ReCAPTCHA> = useRef();


    const contactForm = new ContactFormModel();

    const [loading, setLoading] = useState(false)
    const [disabled, setDisabled] = useState(false)
    const formValuesRef = useRef<ContactFormModel>(null);

    const handleSubmit = (values: ContactFormModel, { setSubmitting }) => {
        setTimeout(async () => {
            values.prefferedLocale = locale;
            formValuesRef.current = values;

            setSubmitting(false);
            setLoading(true)
            setDisabled(true)

            reCAPTCHARef.current.execute();

        }, 400);
    }

    const reCAPTCHAOnExecute = async (captchaCode) => {
        try {
            await postContact(formValuesRef.current, captchaCode)
            router.replace({
                pathname: `${Routes.CONTACT_CONFIRM}`,
            })
        } catch (err) {
            console.log(err)
            Sentry.captureException(err);
        } finally {
            // Reset the reCAPTCHA when the request has failed or succeeeded
            // so that it can be executed again if user submits another email.
            reCAPTCHARef.current.reset();
            formValuesRef.current = null;
            setLoading(false)
            setDisabled(false)
        }
    }

    const reCAPTCHAOnFail = async () => {
        setLoading(false)
    }


    return (
        <>
            <Seo pageKey="contact" />
            <div className="bg-gray-100">
                <PageLayout py="lg" width="md">
                    <Panel title={t('contact:title')} titleSize="lg">

                        <Formik
                            initialValues={contactForm}
                            validationSchema={ContactFormModel.validationSchema}
                            onSubmit={handleSubmit}
                        >

                            <Form>
                                {/* <RadioGroupControl name="gender"
                                    options={genderOptions}
                                /> */}
                                <div className="flex flex-col justify-between lg:flex-row lg:gap-6">
                                    <InputTextControl name="firstName"
                                        label={t('common:personalInformations.firstName')}
                                        required
                                    />
                                    <InputTextControl name="lastName"
                                        label={t('common:personalInformations.lastName')}
                                        required
                                    />
                                </div>

                                <InputTextControl name="email"
                                    label={t('common:personalInformations.emailAddress')}
                                    required
                                />

                                <InputTextControl name="phone"
                                    label={t('common:personalInformations.phone')}
                                    htmlType="tel"
                                />

                                <InputTextareaControl name="message"
                                    label={t('contact:message')}
                                    required
                                    rows={8}
                                    placeholder={t('contact:messagePlaceholder')}
                                />

                                <ErrorFocus />

                                <RetryableReCAPTCHA
                                    ref={reCAPTCHARef}
                                    size="invisible"
                                    onExecute={reCAPTCHAOnExecute}
                                    onFail={reCAPTCHAOnFail}
                                />
                                <Button htmlType="submit" className="w-full mt-10 primary" loading={loading} disabled={disabled}>
                                    {t('contact:send')}
                                </Button>

                            </Form>
                        </Formik>
                    </Panel>
                </PageLayout>
            </div>
            <Footer />
        </>
    )
}

export default ContactPage


export const getStaticProps = async ({ locale }) => ({
    props: {
        ...await serverSideTranslations(locale, [...defaultNs, 'contact']),
    }
})